import React, { useState, useEffect } from "react";
import axios from "axios";
import RiskRow from "./RiskRow";
import RiskFilters from "./RiskFilters";
import RiskPagination from "./RiskPagination";
import AddRiskModal from "./AddRiskModal";
import AddRessourceModal from "./AddRessourceModal";
import AddMenaceModal from "./AddMenaceModal";
import EditRiskModal from "./EditRiskModal";
import AddMesureModal from "./AddMesureModal";
import AssociateMesureModal from "./AssociateMesureModal";
import AddTreatmentPlanModal from "./AddTreatmentPlanModal";
import EditTreatmentPlanModal from "./EditTreatmentPlanModal";
import ConfirmModal from "../ConfirmModal";
import { Toast, ToastContainer } from "react-bootstrap";
import "./RiskPagination.css";
import "./RiskView.css";

const RiskTable = ({ userCountryCode }) => {
  const [risks, setRisks] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    risk_level: "",
    country: "",
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [totalAll, setTotalAll] = useState(0);

  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showRessourceModal, setShowRessourceModal] = useState(false);
  const [showMenaceModal, setShowMenaceModal] = useState(false);
  const [editRiskData, setEditRiskData] = useState(null);
  const [deletePending, setDeletePending] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMesureModal, setShowMesureModal] = useState(false);
  const [mesureTarget, setMesureTarget] = useState(null);
  const [showAssociateModal, setShowAssociateModal] = useState(false);
  const [showAddTreatmentPlan, setShowAddTreatmentPlan] = useState({ show: false, risk: null });

  const [editTreatmentPlan, setEditTreatmentPlan] = useState({ show: false, planId: null });

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

  const fetchData = async (keepExpandedId = null) => {
    const params = {
      page,
      search: searchQuery,
      ...filters,
    };

    try {
      const res = await axios.get("/api/risks.php", { params });
      setRisks(res.data.risks || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalFiltered(res.data.total_filtered || 0);
      setTotalAll(res.data.total_all || 0);

      if (keepExpandedId !== null) {
        setExpanded({ [keepExpandedId]: true });
      } else {
        setExpanded({});
      }
    } catch (err) {
      console.error("Risk fetch error:", err);
      showToast("Error loading risks", "danger");
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, searchQuery, filters]);

  const toggleCollapse = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      risk_level: "",
      country: "",
    });
    setSearchQuery("");
    setPage(1);
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.post("/api/delete_risk.php", { id });
      if (res.data.success) {
        showToast("Risk deleted", "success");
        fetchData();
      } else {
        showToast(res.data.error || "Failed to delete", "danger");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Server error while deleting", "danger");
    }
  };

  const handleEditSuccess = (message = "Risk updated") => {
    fetchData(editRiskData?.id);
    showToast(message, "success");
    setEditRiskData(null);
  };

  return (
    <div className="risk-wrapper mx-auto mt-4">
      <RiskFilters
        filters={filters}
        handleChange={handleChange}
        resetFilters={resetFilters}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        handleCollapseReset={() => setExpanded({})}
        totalCount={totalFiltered}
        totalAll={totalAll}
        onAddNew={() => setShowAddModal(true)}
        onAddRessource={() => setShowRessourceModal(true)}
        onAddMenace={() => setShowMenaceModal(true)}
        onAddMesure={() => setShowMesureModal(true)}
      />

      <table className="table table-dark table-bordered" style={{ border: "1px solid #333" }}>
        <thead>
          <tr>
            {["Risk Title", "Country", "Business Unit", "Responsible", "Criticality"].map((title, index) => {
              const widths = [400, 80, 300, 200, 100];
              return (
                <th
                  key={index}
                  style={{
                    backgroundColor: "#1e1e1e",
                    color: "#f8f9fa",
                    minWidth: `${widths[index]}px`,
                    maxWidth: `${widths[index]}px`,
                    padding: "10px",
                    verticalAlign: "middle",
                  }}
                >
                  {title}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {risks.length > 0 ? (
            risks.map((risk) => (
              <RiskRow
                key={risk.id}
                risk={risk}
                isExpanded={!!expanded[risk.id]}
                toggleCollapse={() => toggleCollapse(risk.id)}
                onDelete={(r) => {
                  setDeletePending(r);
                  setShowDeleteModal(true);
                }}
                onEdit={(r) => setEditRiskData(r)}
                onRefresh={fetchData}
                showToast={showToast}
                userCountry={userCountryCode}
                onRequestDelete={(r) => {
                  setDeletePending(r);
                  setShowDeleteModal(true);
                }}
                onAddMesure={(r) => {
                  setMesureTarget(r);
                  setShowAssociateModal(true);
                }}
                onAddTreatment={(r) => setShowAddTreatmentPlan({ show: true, risk: r })}
                onEditTreatment={(plan) => setEditTreatmentPlan({ show: true, planId: plan.id })}
              />
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center text-light py-4">
                No risks found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <RiskPagination
        page={page}
        totalPages={totalPages}
        onPageChange={(newPage) => setPage(newPage)}
      />

      <AddRiskModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={fetchData}
        showToast={showToast}
      />

      <AddRessourceModal
        show={showRessourceModal}
        onClose={() => setShowRessourceModal(false)}
        onCreated={() => showToast("Resource created!")}
        showToast={showToast}
      />

      <AddMenaceModal
        show={showMenaceModal}
        onClose={() => setShowMenaceModal(false)}
        onCreated={() => showToast("Threat created!")}
        showToast={showToast}
      />

      <EditRiskModal
        show={!!editRiskData}
        data={editRiskData}
        onClose={() => setEditRiskData(null)}
        onSuccess={handleEditSuccess}
        showToast={showToast}
      />

      <AddMesureModal
        show={showMesureModal}
        onClose={() => setShowMesureModal(false)}
        onCreated={() => showToast("Measure added!")}
        showToast={showToast}
      />

      <AssociateMesureModal
        show={showAssociateModal}
        risk={mesureTarget}
        onClose={() => {
          setShowAssociateModal(false);
          setMesureTarget(null);
        }}
        onAssociated={() => {
          showToast("Measure associated!");
          fetchData(mesureTarget?.id);
        }}
        showToast={showToast}
      />

      <AddTreatmentPlanModal
        show={showAddTreatmentPlan.show}
        risk={showAddTreatmentPlan.risk}
        onClose={() => setShowAddTreatmentPlan({ show: false, risk: null })}
        onCreated={() => {
          showToast("Treatment plan added!");
          fetchData(showAddTreatmentPlan.risk?.id);
        }}
        showToast={showToast}
      />

      <EditTreatmentPlanModal
        show={editTreatmentPlan.show}
        planId={editTreatmentPlan.planId}
        onClose={() => setEditTreatmentPlan({ show: false, planId: null })}
        onUpdated={() => {
          showToast("Treatment plan updated!");
          fetchData();
        }}
        showToast={showToast}
      />

      <ConfirmModal
        show={showDeleteModal}
        title="Confirm deletion"
        message={`Permanently delete risk "${deletePending?.name}"?`}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeletePending(null);
        }}
        onConfirm={async () => {
          await handleDelete(deletePending.id);
          setShowDeleteModal(false);
          setDeletePending(null);
        }}
      />

      <ToastContainer
        position="bottom-end"
        className="p-3"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 9999,
        }}
      >
        <Toast
          show={toast.show}
          bg={toast.variant}
          onClose={() => setToast({ ...toast, show: false })}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default RiskTable;
